import React, {
  useContext,
  useState,
  useRef,
  useCallback,
  ChangeEvent,
  useEffect,
} from "react";
import debounce from "lodash/debounce";

import styles from "./ControlPanel.module.css";

import { MapContext } from "../contexts/MapContextProvider";
import GeoCodingSuggestPanel from "./GeoCodingSuggestPanel";
import { minToHours } from "../utils/minToHours";

export type ISuggestedLocation = {
  name: string;
  location: [number, number];
  id: number;
};

export const ControlPanel = ({
  setShowBanner,
}: {
  setShowBanner: (input: string) => void;
}) => {
  const textInputFromRef = useRef<HTMLInputElement>(null);
  const textInputToRef = useRef<HTMLInputElement>(null);
  const [suggestedLocations, setSuggestedLocations] = useState<
    ISuggestedLocation[] | null
  >(null);
  const [activeRowId, setActiveRowId] = useState<null | number>(null);

  const {
    setFindAlternatives,
    animate,
    setAnimate,
    findAlternatives,
    loading,
    travelType,
    setTravelType,
    routeInfo,
    markers,
    setMarkers,
    fromInput,
    setFromInput,
    toInput,
    setToInput,
    showSuggestPanel,
    setShowSuggestPanel,
  } = useContext(MapContext);

  // console.log("ControlPanel");

  const debounceSearch = useCallback(debounce(lookUpLocation, 380), []);
  async function lookUpLocation(query: string) {
    if (query.length > 0) {
      try {
        const raw = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`
        );
        const response = await raw.json();

        const suggestedLocations = response.features.map((feature: any) => ({
          name: feature!.place_name,
          location: [feature.center[1], feature.center[0]],
          id: feature.id,
        }));

        setSuggestedLocations(suggestedLocations);
        // console.log(suggestedLocations);
        // console.log(response);
      } catch (error) {
        setShowBanner("Something went wrong during fetch.");
        // console.log("Error: ", error.message);
      }
    }
  }

  const closeSuggestPanel = useCallback(() => {
    setActiveRowId(null);
    setShowSuggestPanel!(null);
  }, [setShowSuggestPanel]);

  const handleRowClick = useCallback(
    (i: number) => {
      let tempMarkers = [...markers!];
      const newMarker: [number, number] = suggestedLocations![i].location;

      if (showSuggestPanel?.top === textInputFromRef.current!.offsetTop) {
        setFromInput!(suggestedLocations![i].name);
        setSuggestedLocations(null);

        tempMarkers[0] = newMarker;
        setMarkers!(tempMarkers);
      } else {
        setToInput!(suggestedLocations![i].name);
        setSuggestedLocations(null);

        tempMarkers[1] = newMarker;
        setMarkers!(tempMarkers);
      }
      closeSuggestPanel();
    },
    [
      closeSuggestPanel,
      markers,
      setFromInput,
      setMarkers,
      setToInput,
      showSuggestPanel,
      suggestedLocations,
    ]
  );

  function openSuggestPanel(name: "fromInput" | "toInput") {
    if (name === "fromInput") {
      setShowSuggestPanel!({
        top: textInputFromRef.current!.offsetTop,
        left: textInputFromRef.current!.offsetLeft,
        height: textInputFromRef.current!.offsetHeight,
      });
    } else {
      setShowSuggestPanel!({
        top: textInputToRef.current!.offsetTop,
        left: textInputToRef.current!.offsetLeft,
        height: textInputToRef.current!.offsetHeight,
      });
    }
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    e.stopPropagation();
    e.preventDefault();

    const value = e.target.value;

    if (value.length === 0) {
      closeSuggestPanel();
    }

    if (e.target.name === "fromInput") {
      setFromInput!(value);
      !showSuggestPanel && openSuggestPanel("fromInput");
    } else {
      setToInput!(value);
      !showSuggestPanel && openSuggestPanel("toInput");
    }

    debounceSearch(value);
  }

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (showSuggestPanel) {
        if (e.key === "Escape") {
          closeSuggestPanel();
        } else if (e.key === "ArrowDown") {
          setActiveRowId((p) => {
            if (p === null) {
              return 0;
            } else if (p + 1 > suggestedLocations!.length - 1) {
              return 0;
            } else {
              return p + 1;
            }
          });
        } else if (e.key === "ArrowUp") {
          setActiveRowId((p) => {
            if (p === null) {
              return suggestedLocations!.length - 1;
            } else if (p - 1 < 0) {
              return suggestedLocations!.length - 1;
            } else {
              return p - 1;
            }
          });
        } else if (e.key === "Enter") {
          activeRowId !== null && handleRowClick(activeRowId);
        }
      }
    },
    [
      activeRowId,
      closeSuggestPanel,
      handleRowClick,
      showSuggestPanel,
      suggestedLocations,
    ]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div className={styles.container}>
      <input
        type="text"
        className={styles.textInput}
        placeholder="From"
        name="fromInput"
        ref={textInputFromRef}
        value={fromInput}
        onFocus={() => {
          openSuggestPanel("fromInput");

          if (fromInput!.length > 0) {
            setSuggestedLocations(null);
            debounceSearch(fromInput!);
          }
        }}
        onBlur={() => activeRowId && setActiveRowId(null)}
        onChange={handleChange}
        autoComplete="off"
      />
      <input
        type="text"
        ref={textInputToRef}
        className={styles.textInput}
        name="toInput"
        placeholder="To"
        onFocus={() => {
          openSuggestPanel("toInput");

          if (toInput!.length > 0) {
            setSuggestedLocations(null);
            debounceSearch(toInput!);
          }
        }}
        value={toInput}
        onBlur={() => activeRowId && setActiveRowId(null)}
        onChange={handleChange}
        disabled={markers!.length < 1}
        autoComplete="off"
      />

      <div>
        <div className={styles.row}>
          <select
            className={styles.selectElement}
            value={travelType}
            onChange={(e) => setTravelType!(e.target.value)}
          >
            <option value="car">Car</option>
            <option value="bike">Bike</option>
            <option value="foot">Foot</option>
          </select>

          <div className={styles.routeInfo}>
            {`${
              routeInfo!.distance ? (routeInfo!.distance / 1000).toFixed(2) : 0
            } km | ${
              routeInfo!.duration ? minToHours(routeInfo!.duration) : 0
            } min`}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.checkboxes}>
            <label htmlFor="">Alternative</label>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={!!findAlternatives}
              onChange={() => setFindAlternatives!(!findAlternatives)}
              disabled={animate}
            />
            {/* <label htmlFor="">Animate</label>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={!!animate}
              onChange={() => setAnimate!(!animate)}
            /> */}
          </div>

          <div>
            {loading && (
              <div className={`${styles.routeInfo} ${styles.loading}`}>
                Loading...
              </div>
            )}
          </div>
        </div>
      </div>

      {showSuggestPanel && (
        <GeoCodingSuggestPanel
          position={showSuggestPanel}
          suggestedLocations={suggestedLocations}
          handleRowClick={handleRowClick}
          activeRowId={activeRowId}
        />
      )}
    </div>
  );
};

export default React.memo(ControlPanel);
